import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectProps,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import React from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'

type Options = {
    value: string
    label: React.ReactNode | (() => React.ReactNode)
    disabled?: boolean
}

type FormsProps = Omit<UseControllerProps, 'control'> &
    Omit<SelectProps, 'variant'> & {
        children?: React.ReactNode
        name: string
        label: string
        options: Options[] | undefined
        variant?: 'filled' | 'outlined' | 'standard'
        defaultValue?: string
    }

const useStyle = makeStyles(() => ({
    selectMenu: {
        height: 'auto',
        maxHeight: '20rem',
    },
}))

export const FormFieldSelect: React.FC<FormsProps> = ({
    name,
    rules,
    options,
    label,
    variant = 'filled',
    defaultValue = '',
    onChange,
    shouldUnregister,
    children,
    ...props
}) => {
    const { control } = useFormContext()
    const disabled = props.disabled || (control as any)._options?.disabled
    const classes = useStyle()

    return (
        <Controller
            name={name}
            rules={rules}
            control={control}
            defaultValue={defaultValue}
            shouldUnregister={shouldUnregister}
            render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                    <InputLabel id={`${label}-select-label`} variant={variant}>
                        {label}
                        {props.required ? ' *' : ''}
                    </InputLabel>
                    <Select
                        {...props}
                        {...field}
                        disabled={disabled}
                        defaultValue={defaultValue}
                        onChange={(event, child) => {
                            field.onChange(event)
                            if (onChange) {
                                onChange(event, child)
                            }
                        }}
                        labelId={`${label}-select-label`}
                        id={`${label}-select`}
                        label={label}
                        error={!!error}
                        variant={variant}
                        MenuProps={{
                            PopoverClasses: {
                                paper: classes.selectMenu,
                                root: classes.selectMenu,
                            },
                        }}
                    >
                        {children}
                        {options?.map(({ value, label, disabled }) => (
                            <MenuItem
                                key={value}
                                value={value}
                                disabled={disabled}
                            >
                                {typeof label === 'function' ? label() : label}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText error={!!error}>
                        {error ? error.message : null}
                    </FormHelperText>
                </FormControl>
            )}
        />
    )
}
